import { PDFDocument, rgb, StandardFonts, PDFFont, PDFPage } from 'pdf-lib';
import QRCode from 'qrcode';

import { VoucherDetails } from '@/api/voucher/voucher.types';
import { config } from '@/config/config.ts';
import { formatZAR } from '@/utils/valueFormatter.ts';

const formatDate = (unixTimestamp: number): string => {
  const date = new Date(unixTimestamp * 1000);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatGeneratedDate = (): string => {
  const date = new Date();
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const clean = hex.replace('#', '');
  return {
    r: parseInt(clean.substring(0, 2), 16) / 255,
    g: parseInt(clean.substring(2, 4), 16) / 255,
    b: parseInt(clean.substring(4, 6), 16) / 255,
  };
};

const drawVoucherOnPage = async (
  page: PDFPage,
  details: VoucherDetails,
  yOffset: number,
  fonts: { boldFont: PDFFont; regularFont: PDFFont },
  qrImages: {
    appStoreQrImage: any;
    playStoreQrImage: any;
  },
  logoImage: any,
  backgroundImage: any,
  golferImage: any,
  rightGolferImage: any,
  leftGolferImage: any,
  giftVoucherImage: any,
  complimentaryGiftVoucherImage: any
): Promise<void> => {
  const { boldFont, regularFont } = fonts;
  const voucherWidth = 800;
  const voucherHeight = 350;

  const white = rgb(1, 1, 1);

  const darkGreen = rgb(
    hexToRgb('2A3C35').r,
    hexToRgb('2A3C35').g,
    hexToRgb('2A3C35').b
  );

  const labelColor = rgb(
    hexToRgb('374451').r,
    hexToRgb('374451').g,
    hexToRgb('374451').b
  );

  const hostedByColor = rgb(0.45, 0.51, 0.56);
  const black = rgb(0, 0, 0);

  const leftWidth = 280;

  page.drawImage(backgroundImage, {
    x: 0,
    y: yOffset,
    width: leftWidth,
    height: voucherHeight,
  });

  page.drawRectangle({
    x: 0,
    y: yOffset,
    width: leftWidth,
    height: voucherHeight * 0.25,
    color: rgb(0.043, 0.122, 0.09),
    opacity: 0.85,
  });
  page.drawRectangle({
    x: 0,
    y: yOffset + voucherHeight * 0.25,
    width: leftWidth,
    height: voucherHeight * 0.25,
    color: rgb(0.043, 0.122, 0.09),
    opacity: 0.75,
  });
  page.drawRectangle({
    x: 0,
    y: yOffset + voucherHeight * 0.5,
    width: leftWidth,
    height: voucherHeight * 0.5,
    color: rgb(0.043, 0.122, 0.09),
    opacity: 0.35,
  });

  page.drawRectangle({
    x: leftWidth,
    y: yOffset,
    width: voucherWidth - leftWidth,
    height: voucherHeight,
    color: white,
  });

  const golferH = voucherHeight * 0.92;
  const golferW = (golferImage.width / golferImage.height) * golferH;

  const leftGolferH = voucherHeight * 0.95;
  const leftGolferW =
    (leftGolferImage.width / leftGolferImage.height) * golferH;

  const rightGolferH = voucherHeight * 0.65;
  const rightGolferW =
    (rightGolferImage.width / rightGolferImage.height) * rightGolferH;

  const centerGolferX = leftWidth - golferW * 0.4;

  page.drawImage(leftGolferImage, {
    x: centerGolferX - leftGolferW * 0.5,
    y: yOffset,
    width: leftGolferW,
    height: leftGolferH,
    opacity: 1,
  });

  page.drawImage(rightGolferImage, {
    x: centerGolferX + rightGolferW * 0.25,
    y: yOffset,
    width: rightGolferW,
    height: rightGolferH,
    opacity: 1,
  });

  page.drawImage(golferImage, {
    x: centerGolferX,
    y: yOffset,
    width: golferW,
    height: golferH,
    opacity: 1,
  });

  const isComplimentary = details.type === 'complimentary';

  const logoWidth = 160;
  const logoHeight = (logoImage.height / logoImage.width) * logoWidth;
  const logoY = isComplimentary
    ? yOffset + voucherHeight - logoHeight - 25
    : yOffset + voucherHeight - logoHeight - 45;

  page.drawImage(logoImage, {
    x: (leftWidth - logoWidth) / 2,
    y: logoY,
    width: logoWidth,
    height: logoHeight,
  });

  if (isComplimentary) {
    const complimentaryTextLines = [
      'Sign up to redeem your voucher.',
      'Scan the QR code to download the app.',
      '',
      'Once installed, create your profile, and',
      'redeem this voucher when booking a round.',
    ];

    const fontSize = 8;
    const lineHeight = 10;
    const totalLinesHeight = complimentaryTextLines.length * lineHeight;

    const logoBottomY = logoY;
    const qrSizeComplimentary = 45;
    const qrBottomPaddingComplimentary = 15;
    const qrStartYComplimentary = yOffset + qrBottomPaddingComplimentary + 15;
    const qrTopY = qrStartYComplimentary + qrSizeComplimentary + 4;
    const midY = (logoBottomY + qrTopY) / 2;

    complimentaryTextLines.forEach((line, index) => {
      if (line) {
        const lineY = midY + totalLinesHeight / 2 - (index + 0.5) * lineHeight;
        const textWidth = regularFont.widthOfTextAtSize(line, fontSize);
        page.drawText(line, {
          x: leftWidth / 2 - textWidth / 2,
          y: lineY,
          size: fontSize,
          font: regularFont,
          color: white,
        });
      }
    });
  }

  const drawCenteredText = (
    text: string,
    centerX: number,
    y: number,
    size: number
  ): void => {
    const textWidth = regularFont.widthOfTextAtSize(text, size);
    page.drawText(text, {
      x: centerX - textWidth / 2,
      y,
      size,
      font: regularFont,
      color: white,
    });
  };

  const qrSize = isComplimentary ? 45 : 60;
  const qrGap = 20;

  const qrBottomPadding = isComplimentary ? 15 : 25;
  const qrStartY = yOffset + qrBottomPadding + (isComplimentary ? 15 : 20);

  const qrGroupWidth = qrSize * 2 + qrGap;
  const qrGroupStartX = (leftWidth - qrGroupWidth) / 2;

  const iosQrX = qrGroupStartX;
  const androidQrX = qrGroupStartX + qrSize + qrGap;

  const qrBorderPadding = 4;

  const drawQrWithBorder = (image: any, x: number, y: number): void => {
    page.drawRectangle({
      x: x - qrBorderPadding,
      y: y - qrBorderPadding,
      width: qrSize + qrBorderPadding * 2,
      height: qrSize + qrBorderPadding * 2,
      color: white,
      borderColor: rgb(0.9, 0.9, 0.9),
      borderWidth: 1,
    });

    page.drawImage(image, {
      x,
      y,
      width: qrSize,
      height: qrSize,
    });
  };

  drawQrWithBorder(qrImages.appStoreQrImage, iosQrX, qrStartY);
  drawQrWithBorder(qrImages.playStoreQrImage, androidQrX, qrStartY);

  const labelYOffset = isComplimentary ? 18 : 22;
  drawCenteredText('iOS App', iosQrX + qrSize / 2, qrStartY - labelYOffset, 10);

  drawCenteredText(
    'Android App',
    androidQrX + qrSize / 2,
    qrStartY - labelYOffset,
    10
  );

  const rightStartX = leftWidth + 50;
  const rightEndX = voucherWidth - 25;

  const giftVoucherImageY = yOffset + voucherHeight - 100;

  page.drawImage(
    isComplimentary ? complimentaryGiftVoucherImage : giftVoucherImage,
    {
      x: rightStartX + 100,
      y: isComplimentary ? giftVoucherImageY - 55 : giftVoucherImageY,
      height: isComplimentary ? 130 : 85,
      width: isComplimentary ? 330 : 300,
    }
  );

  const eventY = giftVoucherImageY - 35;
  const hostedByY = eventY - 20;

  if (!isComplimentary) {
    page.drawText(details.name, {
      x: rightStartX + 100,
      y: eventY,
      size: 30,
      font: boldFont,
      color: darkGreen,
    });

    if (details.companyName) {
      page.drawText(`Hosted by ${details.companyName}`, {
        x: rightStartX + 100,
        y: hostedByY,
        size: 12,
        font: boldFont,
        color: hostedByColor,
        opacity: 0.4,
      });
    }
  }

  const codeY = hostedByY - 55;
  const codeBoxWidth = 220;
  const codeBoxHeight = 30;
  const codeText = details.code;
  const codeTextSize = 15;

  page.drawRectangle({
    x: rightStartX + 100,
    y: codeY,
    width: codeBoxWidth,
    height: codeBoxHeight,
    color: rgb(0.97, 0.97, 0.97),
    borderColor: rgb(0.75, 0.75, 0.75),
    borderWidth: 1,
  });

  const codeTextWidth = boldFont.widthOfTextAtSize(codeText, codeTextSize);
  page.drawText(codeText, {
    x: rightStartX + 100 + (codeBoxWidth - codeTextWidth) / 2,
    y: codeY + 10,
    size: codeTextSize,
    font: boldFont,
    color: darkGreen,
  });

  const col1X = rightStartX + 100;
  const col2X = col1X + 140;
  const col1Y = codeY - 30;
  const col2Y = col1Y - 50;

  const labelSize = 10;
  const valueSize = 15;

  page.drawText('EVENT DATE', {
    x: col1X,
    y: col1Y,
    size: labelSize,
    font: boldFont,
    color: labelColor,
    opacity: 0.4,
  });

  page.drawText(formatDate(details.validFromUnix), {
    x: col1X,
    y: col1Y - 19,
    size: valueSize,
    font: boldFont,
    color: darkGreen,
  });

  page.drawText('REWARD TIER', {
    x: col2X,
    y: col1Y,
    size: labelSize,
    font: boldFont,
    color: labelColor,
    opacity: 0.4,
  });

  page.drawText(formatZAR(details.rewardTierPrize), {
    x: col2X,
    y: col1Y - 19,
    size: valueSize,
    font: boldFont,
    color: darkGreen,
  });

  const courseName =
    details.golfCourses.length > 0
      ? details.golfCourses[0].name
      : 'Multiple Courses Available';

  page.drawText('GOLF COURSE', {
    x: col1X,
    y: col2Y,
    size: labelSize,
    font: boldFont,
    color: labelColor,
    opacity: 0.4,
  });

  page.drawText(courseName, {
    x: col1X,
    y: col2Y - 19,
    size: valueSize,
    font: boldFont,
    color: darkGreen,
  });

  const generatedText = `Generated ${formatGeneratedDate()}`;
  const generatedSize = 11;
  const generatedWidth = regularFont.widthOfTextAtSize(
    generatedText,
    generatedSize
  );
  page.drawText(generatedText, {
    x: rightEndX - generatedWidth,
    y: yOffset + 12,
    size: generatedSize,
    font: regularFont,
    color: black,
  });
};

export const generateVouchersBatchPdf = async (
  detailsList: VoucherDetails[]
): Promise<Blob> => {
  const pdfDoc = await PDFDocument.create();
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fonts = { boldFont, regularFont };

  const vouchersPerPage = 3;
  const voucherWidth = 800;
  const voucherHeight = 350;
  const pageHeight = voucherHeight * vouchersPerPage;

  let currentPage: PDFPage | null = null;
  let voucherCountOnPage = 0;

  const appStoreQrDataUrl = await QRCode.toDataURL(config.appStoreLink, {
    width: 300,
    margin: 0,
  });

  const playStoreQrDataUrl = await QRCode.toDataURL(config.playStoreLink, {
    width: 300,
    margin: 0,
  });

  const appStoreQrImage = await pdfDoc.embedPng(appStoreQrDataUrl);
  const playStoreQrImage = await pdfDoc.embedPng(playStoreQrDataUrl);

  const logoBytes = await fetch('/GolfersHonoursLogo.png').then((res) =>
    res.arrayBuffer()
  );
  const logoImage = await pdfDoc.embedPng(logoBytes);

  const backgroundBytes = await fetch('/PDFBackground.png').then((res) =>
    res.arrayBuffer()
  );
  const backgroundImage = await pdfDoc.embedPng(backgroundBytes);

  const golferBytes = await fetch('/Golfer.png').then((res) =>
    res.arrayBuffer()
  );
  const golferImage = await pdfDoc.embedPng(golferBytes);

  const rightGolferBytes = await fetch('/RightGolfer.png').then((res) =>
    res.arrayBuffer()
  );
  const rightGolferImage = await pdfDoc.embedPng(rightGolferBytes);

  const leftGolferBytes = await fetch('/LeftGolfer.png').then((res) =>
    res.arrayBuffer()
  );
  const leftGolferImage = await pdfDoc.embedPng(leftGolferBytes);

  const giftVoucherBytes = await fetch('/GiftVoucher.png').then((res) =>
    res.arrayBuffer()
  );
  const giftVoucherImage = await pdfDoc.embedPng(giftVoucherBytes);

  const complimentaryGiftVoucherBytes = await fetch(
    '/ComplimentaryGiftVoucher.png'
  ).then((res) => res.arrayBuffer());
  const complimentaryGiftVoucherImage = await pdfDoc.embedPng(
    complimentaryGiftVoucherBytes
  );

  for (let i = 0; i < detailsList.length; i += 1) {
    const details = detailsList[i];

    if (currentPage === null || voucherCountOnPage >= vouchersPerPage) {
      currentPage = pdfDoc.addPage([voucherWidth, pageHeight]);
      voucherCountOnPage = 0;
    }

    const yOffset = pageHeight - (voucherCountOnPage + 1) * voucherHeight;

    // eslint-disable-next-line no-await-in-loop
    await drawVoucherOnPage(
      currentPage,
      details,
      yOffset,
      fonts,
      {
        appStoreQrImage,
        playStoreQrImage,
      },
      logoImage,
      backgroundImage,
      golferImage,
      rightGolferImage,
      leftGolferImage,
      giftVoucherImage,
      complimentaryGiftVoucherImage
    );

    if (voucherCountOnPage < vouchersPerPage - 1) {
      currentPage.drawRectangle({
        x: 290,
        y: yOffset,
        width: voucherWidth - 290,
        height: 0.1,
        color: rgb(0, 0, 0),
      });
    }

    voucherCountOnPage += 1;
  }

  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes], { type: 'application/pdf' });
};
